import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const BuyerSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    mobile: '',
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [payout, setPayout] = useState({
    upiId: '',
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
  });

  const [errors, setErrors] = useState({});
  const [savedSection, setSavedSection] = useState('');

  // Get user email from various sources
  const getUserEmail = () => {
    // First try localStorage (most reliable based on previous findings)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.email) {
          return parsedUser.email;
        }
      } catch (e) {
        // Silently handle parsing error
      }
    }
    
    // Then try user.email from AuthContext
    if (user?.email) {
      return user.email;
    }
    
    return null;
  };

  // Fetch user profile data
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authAPI.getProfile();
        console.log('Settings profile full response:', response);
        
        // Extract data from response - handle both { data: { ... } } and directly { ... }
        const userData = response.data?.data || response.data || response;
        console.log('Extracted userData for Settings:', userData);
        
        if (userData && (userData.username || userData.email || userData.phoneNumber)) {
          console.log('Mapping userData to state fields...');
          setProfile({
            fullName: userData.username || userData.fullName || '',
            email: userData.email || getUserEmail() || '',
            mobile: userData.phoneNumber || userData.mobile || '',
          });
          
          setPayout({
            upiId: userData.upiId || '',
            bankName: userData.bankName || '',
            accountHolderName: userData.accountHolderName || '',
            accountNumber: userData.bankAccount || '',
            ifscCode: userData.ifscCode || '',
          });
        } else {
          console.warn('userData is empty or missing expected fields:', userData);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        // Fallback to localStorage data
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setProfile({
              fullName: parsedUser.username || '',
              email: parsedUser.email || '',
              mobile: parsedUser.phoneNumber || '',
            });
          } catch (e) {
            console.error('Failed to parse stored user:', e);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSecurityChange = (field, value) => {
    setSecurity((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePayoutChange = (field, value) => {
    setPayout((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateProfile = () => {
    const newErrors = {};

    if (!profile.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!profile.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      newErrors.email = 'Enter a valid email';
    }

    if (!profile.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(profile.mobile)) {
      newErrors.mobile = 'Enter a valid 10-digit mobile number';
    }

    return newErrors;
  };

  const validateSecurity = () => {
    const newErrors = {};

    if (!security.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!security.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (security.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!security.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (security.newPassword !== security.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const validatePayout = () => {
    const newErrors = {};

    if (payout.upiId && !/^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{2,}$/.test(payout.upiId)) {
      newErrors.upiId = 'Enter a valid UPI ID';
    }

    const hasAnyBankField =
      payout.bankName.trim() ||
      payout.accountHolderName.trim() ||
      payout.accountNumber.trim() ||
      payout.ifscCode.trim();

    if (hasAnyBankField) {
      if (!payout.bankName.trim()) {
        newErrors.bankName = 'Bank name is required';
      }

      if (!payout.accountHolderName.trim()) {
        newErrors.accountHolderName = 'Account holder name is required';
      }

      if (!payout.accountNumber.trim()) {
        newErrors.accountNumber = 'Account number is required';
      } else if (!/^\d{9,18}$/.test(payout.accountNumber)) {
        newErrors.accountNumber = 'Enter a valid account number';
      }

      if (!payout.ifscCode.trim()) {
        newErrors.ifscCode = 'IFSC code is required';
      } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(payout.ifscCode.toUpperCase())) {
        newErrors.ifscCode = 'Enter a valid IFSC code';
      }
    }

    return newErrors;
  };

  const [saving, setSaving] = useState(false);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    const newErrors = validateProfile();

    if (Object.keys(newErrors).length) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      setSavedSection('');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        username: profile.fullName,
        phoneNumber: profile.mobile,
        upiId: payout.upiId,
        bankName: payout.bankName,
        bankAccount: payout.accountNumber,
        ifscCode: payout.ifscCode
      };
      
      console.log('Updating buyer profile with data:', updateData);
      await authAPI.updateProfile(updateData);
      toast.success('Settings updated successfully');
      setSavedSection('profile');
      setTimeout(() => setSavedSection(''), 3000);
    } catch (error) {
      console.error('Failed to update settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    const newErrors = validateSecurity();

    if (Object.keys(newErrors).length) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      setSavedSection('');
      return;
    }

    setSaving(true);
    try {
      const role = user?.role?.toUpperCase() || 'BUYER';
      
      await authAPI.resetPassword(
        profile.email,
        security.newPassword,
        security.confirmPassword,
        role
      );
      
      toast.success('Password updated successfully');
      setSavedSection('security');
      setSecurity({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setTimeout(() => setSavedSection(''), 3000);
    } catch (error) {
      console.error('Failed to update password:', error);
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const handlePayoutSave = async (e) => {
    // Re-use handleProfileSave as it now consolidates all fields
    return handleProfileSave(e);
  };

  const cardStyle = {
    background: '#FFFFFF',
    border: '1px solid #DDE3EC',
    borderRadius: '24px',
    padding: '28px',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
  };

  const inputStyle = {
    width: '100%',
    padding: '16px 18px',
    borderRadius: '16px',
    border: '1px solid #D9E1EC',
    background: '#F8FAFC',
    fontSize: '16px',
    color: '#0F172A',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '10px',
    fontSize: '14px',
    fontWeight: 700,
    color: '#667085',
  };

  const sectionTitleStyle = {
    margin: 0,
    fontSize: '24px',
    fontWeight: 800,
    color: '#0F172A',
  };

  const sectionSubStyle = {
    margin: '8px 0 0 0',
    fontSize: '14px',
    color: '#667085',
    lineHeight: 1.5,
  };

  const buttonPrimary = {
    border: 'none',
    borderRadius: '16px',
    padding: '15px 24px',
    background: 'linear-gradient(135deg, #4F46E5 0%, #5B5CEB 100%)',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(79, 70, 229, 0.18)',
  };

  const buttonGreen = {
    border: 'none',
    borderRadius: '16px',
    padding: '15px 24px',
    background: '#16A34A',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 12px 24px rgba(22, 163, 74, 0.18)',
  };

  const errorText = (msg) =>
    msg ? (
      <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '8px' }}>{msg}</div>
    ) : null;

  const successBadge = (section, text) =>
    savedSection === section ? (
      <span
        style={{
          background: '#ECFDF5',
          color: '#15803D',
          border: '1px solid #BBF7D0',
          borderRadius: '999px',
          padding: '8px 12px',
          fontSize: '13px',
          fontWeight: 700,
        }}
      >
        {text}
      </span>
    ) : null;

  return (
    <div title="Settings">
      <div style={{ maxWidth: '1180px', margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{ 
              display: 'inline-block', 
              width: '40px', 
              height: '40px', 
              border: '3px solid #f3f3f3', 
              borderTop: '3px solid #4F46E5', 
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ color: '#6B7280', fontSize: '16px', margin: 0 }}>
              Loading profile data...
            </p>
          </div>
        ) : (
          <>
        <div
          style={{
            ...cardStyle,
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '18px',
            flexWrap: 'wrap',
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '30px', fontWeight: 800, color: '#0F172A' }}>
              Account Settings
            </h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '15px', color: '#667085' }}>
              Manage your profile, security, and payout details from one place.
            </p>
          </div>

          <div
            style={{
              padding: '12px 16px',
              borderRadius: '18px',
              background: '#ECFDF5',
              border: '1px solid #BBF7D0',
              color: '#15803D',
              fontSize: '14px',
              fontWeight: 700,
            }}
          >
            Buyer account active
          </div>
        </div>

        <div style={{ display: 'grid', gap: '24px' }}>
          <form onSubmit={handleProfileSave} style={cardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                flexWrap: 'wrap',
                marginBottom: '24px',
              }}
            >
              <div>
                <h3 style={sectionTitleStyle}>Profile Information</h3>
                <p style={sectionSubStyle}>
                  Update your personal details used for order updates and account communication.
                </p>
              </div>
              {successBadge('profile', 'Profile saved')}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '18px',
              }}
            >
              <div>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  value={profile.fullName}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                  style={inputStyle}
                  placeholder="Enter full name"
                />
                {errorText(errors.fullName)}
              </div>

              <div>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  style={{...inputStyle, backgroundColor: '#e2e8f0', cursor: 'not-allowed', color: '#64748b'}}
                  placeholder="Enter email"
                />
              </div>

              <div>
                <label style={labelStyle}>Mobile Number</label>
                <input
                  type="tel"
                  value={profile.mobile}
                  onChange={(e) => handleProfileChange('mobile', e.target.value)}
                  style={inputStyle}
                  placeholder="Enter mobile number"
                />
                {errorText(errors.mobile)}
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button type="submit" style={buttonPrimary}>
                Save Profile Changes
              </button>
            </div>
          </form>

          <form onSubmit={handlePasswordSave} style={cardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                flexWrap: 'wrap',
                marginBottom: '24px',
              }}
            >
              <div>
                <h3 style={sectionTitleStyle}>Security Settings</h3>
                <p style={sectionSubStyle}>
                  Change your password regularly to keep your account secure.
                </p>
              </div>
              {successBadge('security', 'Password updated')}
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={labelStyle}>Current Password</label>
              <input
                type="password"
                value={security.currentPassword}
                onChange={(e) => handleSecurityChange('currentPassword', e.target.value)}
                style={inputStyle}
                placeholder="Enter current password"
              />
              {errorText(errors.currentPassword)}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '18px',
              }}
            >
              <div>
                <label style={labelStyle}>New Password</label>
                <input
                  type="password"
                  value={security.newPassword}
                  onChange={(e) => handleSecurityChange('newPassword', e.target.value)}
                  style={inputStyle}
                  placeholder="Enter new password"
                />
                {errorText(errors.newPassword)}
              </div>

              <div>
                <label style={labelStyle}>Confirm Password</label>
                <input
                  type="password"
                  value={security.confirmPassword}
                  onChange={(e) => handleSecurityChange('confirmPassword', e.target.value)}
                  style={inputStyle}
                  placeholder="Confirm new password"
                />
                {errorText(errors.confirmPassword)}
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button type="submit" style={buttonPrimary}>
                Update Password
              </button>
            </div>
          </form>

          <form onSubmit={handlePayoutSave} style={cardStyle}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '16px',
                flexWrap: 'wrap',
                marginBottom: '24px',
              }}
            >
              <div>
                <h3 style={sectionTitleStyle}>Payout Details</h3>
                <p style={sectionSubStyle}>
                  Add your UPI or bank account details to receive approved payouts.
                </p>
              </div>
              {successBadge('payout', 'Payout details saved')}
            </div>

            <div
              style={{
                marginBottom: '24px',
                padding: '16px 18px',
                borderRadius: '18px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                color: '#92400E',
                fontSize: '14px',
                lineHeight: 1.5,
              }}
            >
              Please make sure your payout details are correct. Wrong UPI or bank information can delay payments.
            </div>

            <div style={{ marginBottom: '28px' }}>
              <h4
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: 800,
                  color: '#0F172A',
                }}
              >
                UPI Details
              </h4>

              <div>
                <label style={labelStyle}>UPI ID</label>
                <input
                  type="text"
                  value={payout.upiId}
                  onChange={(e) => handlePayoutChange('upiId', e.target.value)}
                  style={inputStyle}
                  placeholder="example@upi"
                />
                {errorText(errors.upiId)}
              </div>
            </div>

            <div
              style={{
                borderTop: '1px solid #E5E7EB',
                paddingTop: '28px',
              }}
            >
              <h4
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: 800,
                  color: '#0F172A',
                }}
              >
                Bank Account Details
              </h4>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '18px',
                }}
              >
                <div>
                  <label style={labelStyle}>Bank Name</label>
                  <input
                    type="text"
                    value={payout.bankName}
                    onChange={(e) => handlePayoutChange('bankName', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter bank name"
                  />
                  {errorText(errors.bankName)}
                </div>

                <div>
                  <label style={labelStyle}>Account Holder Name</label>
                  <input
                    type="text"
                    value={payout.accountHolderName}
                    onChange={(e) => handlePayoutChange('accountHolderName', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter account holder name"
                  />
                  {errorText(errors.accountHolderName)}
                </div>

                <div>
                  <label style={labelStyle}>Account Number</label>
                  <input
                    type="text"
                    value={payout.accountNumber}
                    onChange={(e) => handlePayoutChange('accountNumber', e.target.value)}
                    style={inputStyle}
                    placeholder="Enter account number"
                  />
                  {errorText(errors.accountNumber)}
                </div>

                <div>
                  <label style={labelStyle}>IFSC Code</label>
                  <input
                    type="text"
                    value={payout.ifscCode}
                    onChange={(e) => handlePayoutChange('ifscCode', e.target.value.toUpperCase())}
                    style={inputStyle}
                    placeholder="Enter IFSC code"
                  />
                  {errorText(errors.ifscCode)}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <button type="submit" style={buttonGreen}>
                Save Payout Details
              </button>
            </div>
          </form>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyerSettings;
